import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';
import * as S from '../styles';
import Texts from '../../Texts';
import { MenuItem, CircularProgress, SelectChangeEvent } from '@mui/material';
import apiClient from '../../../api/apiClient';
import { toast } from 'react-toastify';

interface PropsApplicationStepOne {
  id: string;
  display_name: string;
  fa_name: string;
  en_name: string;
  category: string;
  onFieldChange: (field: string, value: unknown) => void;
}

const ApplicationEditStepOne = forwardRef(
  (
    {
      fa_name,
      en_name,
      category,
      onFieldChange,
      id,
      display_name,
    }: PropsApplicationStepOne,
    ref,
  ) => {
    const [categories, setCategories] = useState<
      { id: string; name: string }[]
    >([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [tempFaName, setTempFaName] = useState(fa_name || '');
    const [tempEnName, setTempEnName] = useState(en_name || '');
    const [tempCategory, setTempCategory] = useState(category || '');

    // همگام‌سازی مقادیر اولیه با props
    useEffect(() => {
      setTempFaName(fa_name || '');
      setTempEnName(en_name || '');
      setTempCategory(category || '');
    }, [fa_name, en_name, category]);

    // دریافت دسته‌بندی‌ها از API
    useEffect(() => {
      const fetchCategories = async () => {
        try {
          setLoading(true);
          const response = await apiClient.get(
            '/v2/publisher/applications/category',
          );
          if (response.data && Array.isArray(response.data.categories)) {
            setCategories(response.data.categories);
            // اعتبارسنجی category_id
            if (
              category &&
              !response.data.categories.find(
                (cat: any) => String(cat.id) === String(category),
              )
            ) {
              setError(`دسته‌بندی با شناسه ${category} یافت نشد`);
              toast.error(
                `دسته‌بندی با شناسه ${category} در لیست دسته‌بندی‌ها یافت نشد`,
              );
            } else {
              setError(null);
            }
          } else {
            setError('فرمت داده‌های دریافتی از API نامعتبر است');
            toast.error('فرمت داده‌های دریافتی از API نامعتبر است');
          }
        } catch (err: unknown) {
          if (err instanceof Error) {
            console.error('خطا در دریافت دسته‌بندی‌ها:', err);
            setError(err.message || 'خطا در دریافت دسته‌بندی‌ها');
            toast.error(err.message || 'خطا در دریافت دسته‌بندی‌ها');
          } else {
            console.error('خطای ناشناخته:', err);
            setError('خطا در دریافت دسته‌بندی‌ها');
            toast.error('خطا در دریافت دسته‌بندی‌ها');
          }
        } finally {
          setLoading(false);
        }
      };

      fetchCategories();
    }, [category]);

    // مدیریت تغییرات فیلدهای متنی - اصلاح شده
    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      switch (name) {
        case 'fa_name':
          setTempFaName(value);
          // فوری به والد ارسال کن
          onFieldChange('fa_name', value);
          break;
        case 'en_name':
          setTempEnName(value);
          // فوری به والد ارسال کن
          onFieldChange('en_name', value);
          break;
        default:
          break;
      }
    };

    // مدیریت تغییر سلکت باکس - اصلاح شده
    const handleSelectChange = (e: SelectChangeEvent<unknown>) => {
      const value = e.target.value as string;
      console.log('Selected category:', value);
      setTempCategory(value);
      // فوری به والد ارسال کن
      onFieldChange('category', value);
    };

    // تابع submit برای ارسال داده‌ها به والد
    const handleSubmit = () => {
      if (!tempFaName || !tempEnName || !tempCategory) {
        toast.error('لطفاً تمام فیلدهای الزامی را پر کنید');
        return;
      }
      // این قسمت دیگه ضروری نیست چون فوری ارسال می‌کنیم
      // اما برای اطمینان نگه داشتم
      onFieldChange('fa_name', tempFaName);
      onFieldChange('en_name', tempEnName);
      onFieldChange('category', tempCategory);
    };

    // ارائه تابع submit به والد از طریق ref
    useImperativeHandle(ref, () => ({
      submit: handleSubmit,
    }));

    return (
      // No SBox here: this already sits inside FormSection, so its own white
      // card produced a card-inside-a-card. `direction: rtl` puts the labels
      // and fields on the right, which `align-items: flex-end` alone did not.
      <S.FormRtl>
          <S.LabelInit>{Texts.application.initApp}</S.LabelInit>

          {/* فیلد نام فارسی */}
          <S.Label>{Texts.application.lablefa}</S.Label>
          <S.TextFieldWrapper>
            <S.TextFieldBackground />
            <S.STextField
              sx={{ direction: 'rtl' }}
              id="fa_name"
              name="fa_name"
              type="text"
              placeholder={Texts.application.placeholderfa}
              value={tempFaName}
              onChange={handleTextChange}
            />
          </S.TextFieldWrapper>

          {/* فیلد نام انگلیسی */}
          <S.Label>{Texts.application.lableen}</S.Label>
          <S.TextFieldWrapper>
            <S.TextFieldBackground />
            <S.STextField
              id="en_name"
              name="en_name"
              type="text"
              placeholder={Texts.application.placeholderen}
              value={tempEnName}
              onChange={handleTextChange}
            />
          </S.TextFieldWrapper>

          {/* فیلد دسته‌بندی */}
          <S.Label>{Texts.application.category}</S.Label>
          <S.TextFieldWrapper>
            <S.TextFieldBackground />
            {loading ? (
              <CircularProgress size={24} />
            ) : error ? (
              <div style={{ color: 'red' }}>{error}</div>
            ) : (
              <S.SelectField
                name="category"
                value={tempCategory}
                onChange={handleSelectChange}
                displayEmpty
                inputProps={{ 'aria-label': 'Select category' }}
              >
                <MenuItem value="" disabled>
                  بدون دسته‌بندی
                </MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </S.SelectField>
            )}
          </S.TextFieldWrapper>
      </S.FormRtl>
    );
  },
);

export default ApplicationEditStepOne;
